import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CalculatorIcon, SparklesIcon, TrendingUpIcon } from 'lucide-react';
interface PerformanceZone {
  zone: string;
  emoji: string;
  percentage: number;
  color: string;
  bgGradient: string;
  message: string;
  minThreshold: number;
}
const zones: PerformanceZone[] = [{
  zone: 'Zona Roșie',
  emoji: '🔴',
  percentage: 30,
  color: 'text-red-700',
  bgGradient: 'from-red-100 to-red-200',
  message: 'Sub target – focusează-te pe activitate constantă.',
  minThreshold: 0
}, {
  zone: 'Zona Portocalie',
  emoji: '🟠',
  percentage: 40,
  color: 'text-orange-700',
  bgGradient: 'from-orange-100 to-orange-200',
  message: 'În creștere – încă un efort și ajungi în zona verde!',
  minThreshold: 2450
}, {
  zone: 'Zona Verde 1',
  emoji: '🟢',
  percentage: 45,
  color: 'text-green-700',
  bgGradient: 'from-green-100 to-green-200',
  message: 'Performanță corectă – ești pe drumul cel bun!',
  minThreshold: 3850
}, {
  zone: 'Zona Verde 2',
  emoji: '🟢',
  percentage: 50,
  color: 'text-green-800',
  bgGradient: 'from-green-200 to-green-300',
  message: 'Excelent – ești eligibil pentru bonusuri suplimentare!',
  minThreshold: 4900
}, {
  zone: 'Zona Verde 3',
  emoji: '🟢',
  percentage: 55,
  color: 'text-green-900',
  bgGradient: 'from-green-300 to-green-400',
  message: 'Top Performer – felicitări pentru rezultatele excepționale!',
  minThreshold: 7250
}];
function getZoneForAmount(amount: number): PerformanceZone {
  if (amount >= 7250) return zones[4];
  if (amount >= 4900) return zones[3];
  if (amount >= 3850) return zones[2];
  if (amount >= 2450) return zones[1];
  return zones[0];
}
const quickAmounts = [2000, 3500, 5000, 8000];
export function PerformanceCalculator() {
  const [amount, setAmount] = useState<string>('3500');
  const [showResult, setShowResult] = useState(false);
  const handleCalculate = () => {
    setShowResult(true);
  };
  const amountNum = parseFloat(amount) || 0;
  const zone = getZoneForAmount(amountNum);
  const agentCommission = amountNum * zone.percentage / 100;
  return <div className="space-y-6" data-id="element-2028">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5
    }} className="bg-white rounded-2xl p-8 shadow-xl border-2 border-[#357BEB]" data-id="element-2029">
        <div className="flex items-center space-x-3 mb-8" data-id="element-2030">
          <CalculatorIcon className="w-8 h-8 text-[#357BEB]" data-id="element-2031" />
          <h3 className="text-2xl font-bold text-[#132C53]" data-id="element-2032">
            Calculator de performanță
          </h3>
        </div>
        <div className="space-y-6" data-id="element-2033">
          <div data-id="element-2034">
            <label className="block text-sm font-semibold mb-4 text-[#132C53]" data-id="element-2035">
              Valoare totală comisioane lunare
            </label>
            <div className="space-y-4" data-id="element-2036">
              <div className="flex items-center justify-center" data-id="element-2037">
                <input type="number" value={amount} onChange={e => setAmount(e.target.value)} className="text-5xl font-bold text-center text-[#132C53] bg-transparent border-none focus:outline-none w-full" placeholder="3500" min="0" step="100" data-id="element-2038" />
                <span className="text-4xl font-bold text-[#357BEB] ml-2" data-id="element-2039">
                  €
                </span>
              </div>
              <div className="grid grid-cols-4 gap-3" data-id="element-2040">
                {quickAmounts.map(quickAmount => <motion.button key={quickAmount} whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.95
              }} onClick={() => setAmount(quickAmount.toString())} className={`p-3 rounded-2xl border-2 font-bold transition-all ${amount === quickAmount.toString() ? 'border-[#357BEB] bg-[#357BEB] text-white shadow-lg' : 'border-gray-200 bg-white text-[#132C53] hover:border-[#357BEB] hover:shadow-md'}`} data-id="element-2041">
                    {quickAmount.toLocaleString()}€
                  </motion.button>)}
              </div>
            </div>
          </div>
          <motion.button whileHover={{
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} onClick={handleCalculate} className="w-full bg-gradient-to-r from-[#357BEB] to-[#2868d8] text-white px-6 py-4 rounded-2xl font-bold hover:shadow-xl transition-shadow" data-id="element-2042">
            Calculează zona mea
          </motion.button>
        </div>
      </motion.div>
      <AnimatePresence data-id="element-2043">
        {showResult && <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.9
      }} transition={{
        type: 'spring',
        stiffness: 200,
        damping: 20
      }} className="bg-white rounded-2xl overflow-hidden shadow-xl border-2 border-[#357BEB]" data-id="element-2044">
            <div className="bg-gradient-to-r from-[#357BEB] to-[#2868d8] text-white p-6" data-id="element-2045">
              <div className="flex items-center space-x-2 mb-2" data-id="element-2046">
                <SparklesIcon className="w-6 h-6" data-id="element-2047" />
                <h4 className="text-xl font-bold" data-id="element-2048">Rezultatul calculului</h4>
              </div>
              <p className="text-gray-100" data-id="element-2049">
                Total comisioane lunare: {amountNum.toLocaleString()}€
              </p>
            </div>
            <div className="p-6 space-y-6" data-id="element-2050">
              <motion.div initial={{
            scale: 0.8,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} transition={{
            delay: 0.1
          }} className={`bg-gradient-to-r ${zone.bgGradient} rounded-2xl p-8 border-2 border-gray-200 shadow-lg text-center`} data-id="element-2051">
                <div className="text-6xl mb-4" data-id="element-2052">{zone.emoji}</div>
                <h3 className={`text-3xl font-bold mb-2 ${zone.color}`} data-id="element-2053">
                  {zone.zone}
                </h3>
                <div className="flex items-center justify-center space-x-2 mb-4" data-id="element-2054">
                  <TrendingUpIcon className={`w-6 h-6 ${zone.color}`} data-id="element-2055" />
                  <span className={`text-4xl font-bold ${zone.color}`} data-id="element-2056">
                    {zone.percentage}%
                  </span>
                </div>
                <p className="text-gray-700 text-lg font-medium mb-4" data-id="element-2057">
                  {zone.message}
                </p>
              </motion.div>
              <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-6 border-2 border-green-200" data-id="element-2058">
                <div className="text-center" data-id="element-2059">
                  <p className="text-sm font-medium text-gray-600 mb-2" data-id="element-2060">
                    Comisionul tău lunar
                  </p>
                  <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} transition={{
                delay: 0.5
              }} className="text-4xl font-bold text-green-700" data-id="element-2061">
                    {agentCommission.toLocaleString('ro-RO', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
                    €
                  </motion.div>
                  <p className="text-xs text-gray-600 mt-2" data-id="element-2062">
                    ({amountNum.toLocaleString()}€ × {zone.percentage}%)
                  </p>
                </div>
              </motion.div>
              <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.6
          }} className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-4 text-center" data-id="element-2063">
                <p className="text-sm text-[#132C53] font-medium" data-id="element-2064">
                  💡 Pentru a urca în zona următoare, generează comisioane de
                  minim{' '}
                  {zones.find(z => z.minThreshold > amountNum)?.minThreshold.toLocaleString() || '7.250'}
                  €
                </p>
              </motion.div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}